import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, User, Trash2, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Storage } from '../../lib/storage';

interface CommentSectionProps {
  postId: string;
  isOpen: boolean;
  onClose: () => void;
  onCommentAdded: () => void;
}

export function CommentSection({ postId, isOpen, onClose, onCommentAdded }: CommentSectionProps) {
  const [comentarios, setComentarios] = useState<any[]>([]);
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [loading, setLoading] = useState(false);
  const userId = Storage.get('gravity_user_id');

  const fetchComentarios = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('t_muro_comentarios_gravity')
        .select(`
          *,
          usuarios:usuario_id (nombre, avatar_url)
        `)
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setComentarios(data || []);
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && postId) fetchComentarios();
  }, [isOpen, postId]);

  const handleEnviar = async () => {
    if (!nuevoComentario.trim()) return;
    try {
      const userStr = Storage.get('gravity_user');
      const user = userStr ? JSON.parse(userStr) : {};
      const pin = user.pin;

      if (!pin) {
        alert("Tu sesión de seguridad no tiene un PIN de acceso activo. Por favor, cierra sesión en tu perfil (menú de configuración) y vuelve a ingresar para reactivarlo.");
        return;
      }

      const { error } = await supabase.rpc('comentar_post_gravity_seguro', {
        p_post_id: postId,
        p_usuario_id: userId,
        p_pin: pin,
        p_texto: nuevoComentario
      });

      if (error) throw error;
      
      setNuevoComentario('');
      fetchComentarios();
      onCommentAdded();
    } catch (err) {
      console.error('Error enviando comentario:', err);
    }
  };

  const handleBorrar = async (id: string) => {
    try {
      const userStr = Storage.get('gravity_user');
      const user = userStr ? JSON.parse(userStr) : {};
      const pin = user.pin;

      if (!pin) {
        alert("Tu sesión de seguridad no tiene un PIN de acceso activo. Por favor, cierra sesión en tu perfil (menú de configuración) y vuelve a ingresar para reactivarlo.");
        return;
      }

      const { error } = await supabase.rpc('eliminar_comentario_gravity_seguro', {
        p_comment_id: id,
        p_usuario_id: userId,
        p_pin: pin
      });

      if (error) throw error;
      
      fetchComentarios();
      onCommentAdded();
    } catch (err) {
      console.error('Error borrando comentario:', err);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0, y: '100%' }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: '100%' }}
          className="fixed inset-0 z-[600] bg-black/90 backdrop-blur-xl flex flex-col pt-12"
        >
          <div className="px-6 flex justify-between items-center mb-6">
            <h3 className="text-white font-black text-sm uppercase tracking-widest">Comentarios</h3>
            <button onClick={onClose} className="p-2 bg-white/5 rounded-full"><X className="w-4 h-4 text-slate-500" /></button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 space-y-4 pb-20">
            {loading ? (
              <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 text-primary-cyan animate-spin" /></div>
            ) : comentarios.length > 0 ? (
              comentarios.map((c: any) => (
                <div key={c.id} className="flex gap-3 bg-white/5 p-3 rounded-2xl border border-white/5 relative">
                  <div className="w-8 h-8 rounded-full bg-slate-800 shrink-0 overflow-hidden">
                    {c.usuarios?.avatar_url ? (
                      <img src={c.usuarios.avatar_url} className="w-full h-full object-cover" />
                    ) : <User className="w-full h-full p-2 text-slate-600" />}
                  </div>
                  <div className="flex flex-col flex-1">
                    <span className="text-[10px] font-black text-primary-cyan uppercase mb-1">{c.usuarios?.nombre || 'Piloto'}</span>
                    <p className="text-white text-xs font-medium leading-snug">{c.texto}</p>
                  </div>
                  {c.usuario_id === userId && (
                    <button onClick={() => handleBorrar(c.id)} className="text-red-500/40 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              ))
            ) : (
              <p className="text-center text-slate-600 text-[10px] uppercase font-black py-10">Sin comentarios aún</p>
            )}
          </div>

          {/* INPUT FIJO */}
          <div className="p-4 bg-black border-t border-white/10 flex gap-2">
            <input 
              value={nuevoComentario}
              onChange={e => setNuevoComentario(e.target.value)}
              placeholder="Escribe un comentario..."
              className="flex-1 bg-slate-800/50 border border-white/10 rounded-xl px-4 py-3 text-white text-xs outline-none focus:border-primary-cyan/50"
            />
            <button 
              onClick={handleEnviar}
              disabled={!nuevoComentario.trim()}
              className="w-12 h-12 bg-primary-cyan text-bg-dark rounded-xl flex items-center justify-center disabled:opacity-50 active:scale-95 transition-all"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
